/**
 * Continuous ride layers. Every node is built once; per frame only a few
 * smoothed AudioParam targets move.
 */
import type { CyclingDiscipline, Surface } from '../model';
import { Graph, Smooth } from './graph';
import {
  BIKE_ACOUSTICS, SKID_SURFACE, breathProfile, cadenceRpm, chainLinkRate, clamp01, freehubClickRate, hissCutoff,
  nearestRivals, rivalSpatial, smoothstep, surfaceLayerGains, treadPassRate, windProfile, type TyreGains,
} from './mapping';
import type { AudioRideState } from './types';

/** Per-frame values shared by the layers (computed once by the engine). */
export interface Frame {
  active: boolean;
  speed: number;
  surface: Surface;
  discipline: CyclingDiscipline;
  airborne: boolean;
  airTime: number;
  effort: number;
  brake: number;
  skid: number;
  heartRate: number;
  stamina: number;
}

/** Calibrated mix levels (linear). Tuned with the offline render harness. */
export const LEVELS = {
  hiss: .4, spray: .3, sizzle: .3, crunch: .9, grit: 1, rumble: .07, thump: .3, tread: .2,
  wind: .62, buffet: .8, whirr: .9, hub: .006, squeal: .18, scrub: .75, tyreSqueal: .13, air: .5, breath: .32,
  rivalHiss: .7, rivalHub: .006,
};

export class TyreLayer {
  private hiss: Smooth; private hissLp: Smooth; private rumble: Smooth; private rumbleLp: Smooth; private tread: Smooth; private treadHz: Smooth;
  private sparse: Smooth; private dense: Smooth; private grainRate: Smooth[]; private grit: Smooth; private thump: Smooth; private thumpRate: Smooth;
  private spray: Smooth; private sizzle: Smooth;
  private gains: TyreGains = { hiss: 0, spray: 0, crunch: 0, grit: 0, rumble: 0, thump: 0, tread: 0 };
  /** Current loose-gravel crunch level, read by the pebble scheduler. */
  crunchLevel = 0;
  constructor(g: Graph, readonly out: AudioNode) {
    const c = g.ctx, k = g.kit, sm = (p: AudioParam, tau = .1) => new Smooth(c, p, tau);
    const hissG = g.gain(0, out), lp = g.filter('lowpass', 4000, .5, hissG), hp = g.filter('highpass', 420, .6, lp);
    g.loop(k.white, 1, hp);
    this.hiss = sm(hissG.gain); this.hissLp = sm(lp.frequency, .2);
    const rumbleG = g.gain(0, out), rumbleAm = g.modulated(1, .45, k.texture, 1.3), rumbleLp = g.filter('lowpass', 140, .9, g.filter('highpass', 48, .7, rumbleAm.node));
    rumbleAm.node.connect(rumbleG); g.loop(k.brown, 1, rumbleLp);
    this.rumble = sm(rumbleG.gain); this.rumbleLp = sm(rumbleLp.frequency, .2);
    const treadG = g.gain(0, out), treadBp = g.filter('bandpass', 900, .8, treadG), treadOsc = g.osc(k.wave('tread'), 1, treadBp);
    const jitter = g.gain(28, treadOsc.detune); g.loop(k.texture, 2.1, jitter);
    this.tread = sm(treadG.gain); this.treadHz = sm(treadOsc.frequency, .06);
    const crunchHp = g.filter('highpass', 520, .7, out), sparseG = g.gain(0, crunchHp), denseG = g.gain(0, crunchHp);
    const s1 = g.loop(k.gravelSparse, 1, sparseG), s2 = g.loop(k.gravelDense, 1, denseG);
    this.sparse = sm(sparseG.gain, .12); this.dense = sm(denseG.gain, .12); this.grainRate = [sm(s1.playbackRate, .3), sm(s2.playbackRate, .3)];
    const gritG = g.gain(0, out); g.loop(k.grit, 1, g.filter('highpass', 1600, .7, gritG)); this.grit = sm(gritG.gain, .12);
    const thumpG = g.gain(0, out), loam = g.loop(k.loam, 1, g.filter('lowpass', 900, .7, thumpG));
    this.thump = sm(thumpG.gain, .12); this.thumpRate = sm(loam.playbackRate, .3);
    const sprayG = g.gain(0, out); g.loop(k.spray, 1, g.filter('highpass', 2300, .7, sprayG)); this.spray = sm(sprayG.gain, .12);
    const sizzleG = g.gain(0, out), sizzleAm = g.modulated(.7, .3, k.texture, 3.7); sizzleAm.node.connect(sizzleG);
    g.loop(k.white, 1, g.filter('bandpass', 6800, .6, sizzleAm.node)); this.sizzle = sm(sizzleG.gain, .12);
  }
  apply(f: Frame) {
    const gains = surfaceLayerGains(f.surface, f.speed, f.discipline, this.gains);
    const on = f.active && !f.airborne ? 1 : 0, tau = f.airborne ? .025 : .12;
    const scatter = SKID_SURFACE[f.surface].scatter * f.skid;
    const denseMix = smoothstep(3, 15, f.speed);
    this.crunchLevel = on * (gains.crunch + scatter * .6);
    this.hiss.set(on * gains.hiss * LEVELS.hiss, tau); this.hissLp.set(hissCutoff(f.speed));
    this.rumble.set(on * gains.rumble * LEVELS.rumble, tau); this.rumbleLp.set(95 + f.speed * 12);
    this.tread.set(on * gains.tread * LEVELS.tread, tau); this.treadHz.set(Math.max(1, treadPassRate(f.speed, f.discipline)));
    this.sparse.set(on * gains.crunch * (1 - .55 * denseMix) * LEVELS.crunch, tau);
    this.dense.set(on * (gains.crunch * denseMix + scatter * .8) * LEVELS.crunch, tau);
    const rate = .86 + .3 * clamp01(f.speed / 18);
    this.grainRate.forEach(p => p.set(rate));
    this.grit.set(on * gains.grit * LEVELS.grit, tau);
    this.thump.set(on * gains.thump * LEVELS.thump, tau); this.thumpRate.set(.8 + .35 * clamp01(f.speed / 16));
    this.spray.set(on * gains.spray * LEVELS.spray, tau); this.sizzle.set(on * gains.spray * LEVELS.sizzle, tau);
  }
}

export class WindLayer {
  private level: Smooth; private gust: Smooth; private centres: Smooth[]; private buffet: Smooth; private buffetMod: Smooth;
  constructor(g: Graph, out: AudioNode) {
    const c = g.ctx, k = g.kit;
    const levelG = g.gain(0, out), am = g.modulated(1, .3, k.gust, 1);
    am.node.connect(levelG);
    const bpL = g.filter('bandpass', 500, .55), bpR = g.filter('bandpass', 540, .55);
    g.loop(k.white, 1, bpL); g.loop(k.white, 1, bpR);
    g.stereo(bpL, bpR, .45, am.node);
    this.level = new Smooth(c, levelG.gain, .25); this.gust = new Smooth(c, am.depth.gain, .5);
    this.centres = [new Smooth(c, bpL.frequency, .3), new Smooth(c, bpR.frequency, .3)];
    const buffetG = g.gain(0, out), bam = g.modulated(.6, .4, k.texture, 1.7); bam.node.connect(buffetG);
    g.loop(k.brown, 1, g.filter('lowpass', 85, .8, bam.node));
    this.buffet = new Smooth(c, buffetG.gain, .2); this.buffetMod = new Smooth(c, bam.depth.gain, .3);
  }
  apply(f: Frame) {
    const w = windProfile(f.active ? f.speed : 0, f.airborne);
    this.level.set(w.level * LEVELS.wind, f.airborne ? .08 : .25);
    this.gust.set(.18 + .2 * clamp01(f.speed / 15));
    this.centres[0].set(w.centre); this.centres[1].set(w.centre * 1.08);
    this.buffet.set(w.buffet * LEVELS.buffet * (f.airborne ? .4 : 1)); this.buffetMod.set(.35 + .25 * clamp01(f.speed / 20));
  }
}

export class DrivetrainLayer {
  private whirr: Smooth; private link: Smooth; private stroke: Smooth; private hub: Smooth; private hubRate: Smooth; private hubTone: Smooth[];
  constructor(g: Graph, out: AudioNode) {
    const c = g.ctx, k = g.kit;
    const whirrG = g.gain(0, out), strokeAm = g.gain(.75, whirrG), linkAm = g.gain(.5, strokeAm);
    g.loop(k.white, 1, g.filter('bandpass', 2900, 1.5, linkAm));
    const linkOsc = g.osc(k.wave('link'), 40, g.gain(.5, linkAm.gain));
    const strokeOsc = g.osc('sine', 3, g.gain(.25, strokeAm.gain));
    this.whirr = new Smooth(c, whirrG.gain, .05);
    this.link = new Smooth(c, linkOsc.frequency, .08); this.stroke = new Smooth(c, strokeOsc.frequency, .2);
    const hubG = g.gain(0, out), hubAm = g.modulated(.85, .15, k.texture, 4.3); hubAm.node.connect(hubG);
    const bp1 = g.filter('bandpass', 3500, 7, hubAm.node), bp2 = g.filter('bandpass', 5600, 9, hubAm.node);
    const hubOsc = g.osc(k.wave('impulse'), 20, bp1, g.gain(.6, bp2));
    this.hub = new Smooth(c, hubG.gain, .04); this.hubRate = new Smooth(c, hubOsc.frequency, .05);
    this.hubTone = [new Smooth(c, bp1.frequency, .01), new Smooth(c, bp2.frequency, .01)];
  }
  apply(f: Frame) {
    const bike = BIKE_ACOUSTICS[f.discipline];
    const rolling = f.active && f.speed > .3;
    const pedal = rolling && !f.airborne && f.effort > .05;
    const cadence = cadenceRpm(f.speed, f.discipline, f.effort);
    this.link.set(Math.max(1, chainLinkRate(cadence, f.discipline))); this.stroke.set(cadence / 30);
    const whirr = pedal ? (.35 + .65 * f.effort) * smoothstep(.3, 5, f.speed) : 0;
    this.whirr.set(whirr * LEVELS.whirr);
    const coast = rolling && !pedal;
    const rate = Math.max(1, freehubClickRate(f.speed, f.discipline));
    this.hubRate.set(rate);
    this.hubTone[0].set(bike.hubTone); this.hubTone[1].set(bike.hubTone * 1.62);
    // The impulse train's per-click area falls as 1/rate; scaling by rate keeps every pawl click equally loud.
    this.hub.set(coast ? LEVELS.hub * bike.hubLoudness * smoothstep(.3, 2.5, f.speed) * rate / 100 : 0);
  }
}

export class BrakeLayer {
  private squeal: Smooth; private squealHz: Smooth; private scrub: Smooth; private tyre: Smooth; private tyreHz: Smooth;
  constructor(g: Graph, out: AudioNode) {
    const c = g.ctx, k = g.kit;
    const squealG = g.gain(0, out), warble = g.modulated(.75, .25, k.texture, 5.1); warble.node.connect(squealG);
    const squealOsc = g.osc(k.wave('squeal'), 1800, g.filter('bandpass', 2000, 2.5, warble.node));
    g.osc('sine', 6.3, g.gain(14, squealOsc.detune));
    const scrubG = g.gain(0, out), rough = g.modulated(.65, .35, k.texture, 3.3); rough.node.connect(scrubG);
    g.loop(k.white, 1, g.filter('bandpass', 1300, .8, rough.node));
    const tyreG = g.gain(0, out), tyreOsc = g.osc('sawtooth', 820, g.filter('bandpass', 1050, 2.2, tyreG));
    const tyreJitter = g.gain(55, tyreOsc.detune); g.loop(k.texture, 2.6, tyreJitter);
    this.squeal = new Smooth(c, squealG.gain, .04); this.squealHz = new Smooth(c, squealOsc.frequency, .15);
    this.scrub = new Smooth(c, scrubG.gain, .05); this.tyre = new Smooth(c, tyreG.gain, .05); this.tyreHz = new Smooth(c, tyreOsc.frequency, .2);
  }
  apply(f: Frame) {
    const surface = SKID_SURFACE[f.surface], bike = BIKE_ACOUSTICS[f.discipline];
    const ground = f.active && !f.airborne ? 1 : 0;
    const brake = f.brake * ground, skid = f.skid * ground;
    this.squeal.set(brake ** 1.2 * LEVELS.squeal * (f.speed < 9 ? 1 : .6) * (f.surface === 'wet' ? .5 : 1));
    this.squealHz.set(bike.squeal * (.95 + .1 * clamp01(f.speed / 15)));
    this.scrub.set(clamp01(skid + brake * .45) * surface.scrub * smoothstep(.5, 6, f.speed) * LEVELS.scrub);
    this.tyre.set(skid * surface.squeal * LEVELS.tyreSqueal); this.tyreHz.set(760 + 12 * f.speed);
  }
}

export class AirLayer {
  private level: Smooth; private centre: Smooth;
  constructor(g: Graph, out: AudioNode) {
    const levelG = g.gain(0, out), bp = g.filter('bandpass', 500, 1.1, levelG);
    g.loop(g.kit.white, 1, bp);
    this.level = new Smooth(g.ctx, levelG.gain, .06); this.centre = new Smooth(g.ctx, bp.frequency, .1);
  }
  apply(f: Frame) {
    const rise = f.active && f.airborne ? clamp01(f.airTime / .4) : 0;
    this.level.set(rise * (.35 + .65 * clamp01(f.speed / 18)) * LEVELS.air, rise ? .08 : .03);
    this.centre.set(450 + 2100 * clamp01(f.airTime / 1.3));
  }
}

export class BreathLayer {
  private level: Smooth; private rates: Smooth[];
  constructor(g: Graph, out: AudioNode) {
    const c = g.ctx, k = g.kit, levelG = g.gain(0, out);
    const exAm = g.gain(k.envelopeMean('exhale'), levelG), inAm = g.gain(k.envelopeMean('inhale'), levelG);
    g.loop(k.white, 1, g.filter('bandpass', 1050, 1.1, exAm), g.filter('bandpass', 420, 1.4, g.gain(.6, exAm)));
    g.loop(k.white, 1, g.filter('bandpass', 2300, 1.4, inAm));
    const ex = g.osc(k.wave('exhale'), .35, exAm.gain), inh = g.osc(k.wave('inhale'), .35, inAm.gain);
    this.level = new Smooth(c, levelG.gain, .6); this.rates = [new Smooth(c, ex.frequency, 1.2), new Smooth(c, inh.frequency, 1.2)];
  }
  apply(f: Frame) {
    const b = breathProfile(f.heartRate, f.effort, f.stamina);
    this.level.set(f.active ? b.level * LEVELS.breath : 0);
    this.rates.forEach(r => r.set(b.rate));
  }
}

interface RivalVoice { id: number | null; free: number; gain: Smooth; hiss: Smooth; hub: Smooth; hubRate: Smooth; panner: PannerNode }

/** Pooled, spatialised rival voices (nearest few within range). */
export class RivalVoices {
  private voices: RivalVoice[] = [];
  constructor(private g: Graph, out: AudioNode, count = 4, hrtf = true) {
    const c = g.ctx, k = g.kit;
    for (let i = 0; i < count; i++) {
      const panner = c.createPanner();
      try { panner.panningModel = hrtf ? 'HRTF' : 'equalpower'; } catch { panner.panningModel = 'equalpower'; }
      panner.distanceModel = 'inverse'; panner.refDistance = 3; panner.rolloffFactor = 1.15; panner.maxDistance = 80;
      panner.connect(out);
      const voiceG = g.gain(0, panner), hissG = g.gain(0, voiceG), hubG = g.gain(0, voiceG);
      g.loop(k.white, 1, g.filter('bandpass', 2100, .8, hissG));
      const osc = g.osc(k.wave('impulse'), 30, g.filter('bandpass', 4300 + i * 260, 7, hubG));
      this.voices.push({ id: null, free: 0, gain: new Smooth(c, voiceG.gain, .12), hiss: new Smooth(c, hissG.gain, .1), hub: new Smooth(c, hubG.gain, .06), hubRate: new Smooth(c, osc.frequency, .1), panner });
    }
  }
  apply(state: AudioRideState | null, f: Frame) {
    const t = this.g.ctx.currentTime;
    const near = state && f.active ? nearestRivals(state.rivals, state.distance, state.lateral, this.voices.length) : [];
    for (const v of this.voices) if (v.id !== null && !near.some(r => r.id === v.id)) { v.id = null; v.free = t + .3; v.gain.set(0, .08); }
    for (const r of near) {
      let v = this.voices.find(x => x.id === r.id);
      if (!v) { v = this.voices.find(x => x.id === null && x.free <= t); if (!v) continue; v.id = r.id; }
      const p = rivalSpatial(state!.distance, state!.lateral, r);
      // Plain values (not automation): the panner interpolates internally and stays on its cheap k-rate path.
      if (v.panner.positionX) { v.panner.positionX.value = p.x; v.panner.positionY.value = r.air * .5; v.panner.positionZ.value = p.z; }
      else v.panner.setPosition(p.x, r.air * .5, p.z);
      const grounded = r.air <= 0, coasting = r.pace === 'recover' || !grounded;
      v.gain.set(p.gain);
      v.hiss.set(grounded ? clamp01(r.speed / 18) ** 1.5 * LEVELS.rivalHiss : 0);
      const rate = Math.max(1, freehubClickRate(r.speed, f.discipline));
      v.hub.set(coasting ? smoothstep(1, 4, r.speed) * LEVELS.rivalHub * rate / 100 : 0);
      v.hubRate.set(rate);
    }
  }
}
